import { create } from 'zustand';
import { IndexOrderTextData, TextData } from '../dto/EntityRepository';

function withSequentialIndex(items: IndexOrderTextData[]): IndexOrderTextData[] {
  return items.map((row, i) => ({ ...row, index: i }));
}

function toIndexOrderRow(text: TextData, index: number): IndexOrderTextData {
  return {
    index,
    id: String(text.id),
    title: text.title,
  };
}

interface TextIndexState {
  /** Texts 순서 편집 UI가 열려 있는지 — 모달/오버레이는 이 플래그로 분기 */
  isIndexingMode: boolean;
  /** 인덱스 패널에서 순서/목록이 바뀌었고 아직 적용 안 함 (UNSAVED 처리용) */
  isChanged: boolean;
  /** 인덱싱 모드 on/off — off일 때 선택 목록 비움 */
  setIsIndexingMode: (enabled: boolean) => void;
  selectedIndexingTextList: IndexOrderTextData[];
  // isIndexingMode가 true인 상태에서 TextInfo를 클릭하면 selectedIndexingTextList에 추가
  addSelectedIndexingText: (text: TextData) => void;
  /** IndexWorkViewer mergeIndexingDropAtSlot 과 동일: `dropIndex` 앞 슬롯에 id 들(블록 순서는 현재 목록 순 유지) */
  mergeIndexingDropAtSlot: (draggedIds: string[], dropIndex: number) => void;
  // selectedIndexingTextList에 있던 항목을 클릭하면 목록에서 제거
  removeSelectedIndexingText: (id: string) => void;
  clearSelectedIndexingTextList: () => void;
  /** 서버에서 받은 수동 순서 목록으로 교체 (순번은 0..n-1로 재부여) */
  replaceSelectedIndexingTextList: (list: IndexOrderTextData[]) => void;
  /** CHANGE(저장) 등으로 패널 변경분을 적용 처리한 뒤 플래그만 내림 */
  clearTextIndexingChangedFlag: () => void;
}

export const useTextIndexStore = create<TextIndexState>((set, get) => ({
  isIndexingMode: false,
  isChanged: false,
  setIsIndexingMode: (enabled) =>
    set(
      enabled
        ? { isIndexingMode: true, isChanged: false }
        : { isIndexingMode: false, selectedIndexingTextList: [], isChanged: false }
    ),
  selectedIndexingTextList: [],
  addSelectedIndexingText: (text) => {
    if (!get().isIndexingMode) return;
    const { selectedIndexingTextList } = get();
    if (selectedIndexingTextList.some((row) => String(row.id) === String(text.id))) return;
    const nextIndex = selectedIndexingTextList.length;
    set({
      selectedIndexingTextList: [
        ...selectedIndexingTextList,
        toIndexOrderRow(text, nextIndex),
      ],
      isChanged: true,
    });
  },
  mergeIndexingDropAtSlot: (draggedIds, dropIndex) => {
    if (!get().isIndexingMode) return;
    const orderedDataList = get().selectedIndexingTextList;
    const idKeys = draggedIds.map(String);
    if (idKeys.length === 0) return;

    const movingSet = new Set(idKeys);
    const blockOrder = orderedDataList.filter((item) => movingSet.has(String(item.id)));
    if (blockOrder.length === 0) return;

    const without = orderedDataList.filter((item) => !movingSet.has(String(item.id)));

    let insertAt = 0;
    for (let i = 0; i < dropIndex; i++) {
      const row = orderedDataList[i];
      if (!row) break;
      if (!movingSet.has(String(row.id))) insertAt++;
    }

    const updatedList = [
      ...without.slice(0, insertAt),
      ...blockOrder,
      ...without.slice(insertAt),
    ];
    const nextList = withSequentialIndex(updatedList);
    const unchangedOrder =
      nextList.length === orderedDataList.length &&
      nextList.every((row, i) => String(row.id) === String(orderedDataList[i].id));
    set({
      selectedIndexingTextList: nextList,
      ...(!unchangedOrder ? { isChanged: true } : {}),
    });
  },
  removeSelectedIndexingText: (id) =>
    set((s) => ({
      selectedIndexingTextList: withSequentialIndex(
        s.selectedIndexingTextList.filter((row) => String(row.id) !== String(id))
      ),
      isChanged: true,
    })),
  clearSelectedIndexingTextList: () =>
    set({ isIndexingMode: false, selectedIndexingTextList: [], isChanged: false }),
  replaceSelectedIndexingTextList: (list) =>
    set({ selectedIndexingTextList: withSequentialIndex(list) }),
  clearTextIndexingChangedFlag: () => set({ isChanged: false }),
}));
